// LIBRERIE/IMPORTS
import React, { useState, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';

// CONFIGURAZIONI
import { appConfig } from "../../config/appConfig";
import mapStyles from '../../config/mapStyles.json';

// COMPONENTS
import ServerError from '../Error/ServerError';
import LoadingIndicator from '../Loaders/LoadingIndicator';

const GOOGLE_API_KEY = import.meta.env.VITE_APP_GOOGLE_API_KEY;

const mapCenter = {
    lat: Number(appConfig.map.centerMapData?.center_lat || appConfig.map.defaultCenter.lat),
    lng: Number(appConfig.map.centerMapData?.center_lng || appConfig.map.defaultCenter.lng),
};
const mapZoom = Number(appConfig.map.centerMapData?.zoom || appConfig.map.defaultZoom);

interface MarkerInfo {
  lat: number;
  lng: number;
  name: string;
  adr?: string;
}
interface MapLoaderProps {
    markerInfo: MarkerInfo[] | null;
}

const MapLoader: React.FC<MapLoaderProps> = ({ markerInfo }) => {
    const { t } = useTranslation();
    const mapRef = useRef<google.maps.Map | null>(null);
    const [isInfoWindowOpen, setIsInfoWindowOpen] = useState([false,false]);

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_API_KEY || "",
        version: 'weekly',
        libraries: ['maps', 'marker'],
        language: 'it',
        region: 'IT'
    });

    const getMarkerIcon = useCallback((color: string): google.maps.Icon | undefined => {
        if (!isLoaded || !window.google || !window.google.maps) {
            return undefined;
        }
        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"><path fill="${color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 10c-1.65 0-3-1.35-3-3s1.35-3 3-3 3 1.35 3 3-1.35 3-3 3z"/></svg>`
            )}`,
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 40),
        };
    }, [isLoaded]);

    const handleMarkerClick = (index: number) => {
        setIsInfoWindowOpen(prev => {
            // Crea un nuovo array con lo stesso numero di elementi
            const newState = prev.map((_, i) => {
                // Se siamo sull'indice cliccato, metti true
                if (i === index) {
                    return true;
                } else {
                    return false;
                }
            });

            // Ritorna il nuovo array come nuovo stato
            return newState;
        });
    };
    const handleInfoWindowClose = useCallback(() => setIsInfoWindowOpen([false,false]), []);
    const onLoad = useCallback((map: google.maps.Map) => { mapRef.current = map; }, []);
    const onUnmount = useCallback(() => { mapRef.current = null; }, []);

    // Gestione degli stati di caricamento ed errore
    if (loadError) return <ServerError message={`${t('contactPage.errors.mapLoadFailed')} ${loadError.message}`} />;
    if (!GOOGLE_API_KEY) return <ServerError message="Missing Google Maps API Key." />;

    return (
        <section className="property-map mb-5" data-location="true" style={{ minHeight: '400px' }}>
            <div className="map-canvas img-border">
                {isLoaded ? (
                    <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        center={mapCenter}
                        zoom={mapZoom}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                        options={{ styles: mapStyles, disableDefaultUI: false }}
                    >
                        {markerInfo?.map((marker, index) => (
                            <div key={index}>
                                <Marker
                                    position={{ lat: marker.lat, lng: marker.lng }}
                                    icon={getMarkerIcon(appConfig.map.markerColor || '#FF0000')}
                                    title={marker.name}
                                    onClick={() => handleMarkerClick(index)}
                                />
                                {isInfoWindowOpen && isInfoWindowOpen[index] && (
                                    <InfoWindow
                                    position={{ lat: marker.lat, lng: marker.lng }}
                                    onCloseClick={handleInfoWindowClose}
                                    >
                                        <div>
                                            <h6>{marker.name}</h6>
                                            <p>{marker.adr}</p>
                                        </div>
                                </InfoWindow>
                                )}
                            </div>
                        ))}
                    </GoogleMap>
                ) : (
                    <LoadingIndicator />
                )}
            </div>
        </section>
    );
};

export default MapLoader;